import { useRestClient } from '@/contexts/QueryProvider'
import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { showDatabricksProvisionWarnings } from '@/lib/databricksProvisionWarnings'
import { toast } from 'sonner'
import { Button } from '@fluentui/react-components'
import { Add24Regular, FolderList20Regular, FolderOpen24Regular, Notebook24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { useCurrentApplication } from '../useCurrentApplication'

const statusIcon = (status?: string) => {
  if (status === 'Check') {
    return <span className="text-green-500">✔</span>
  } else if (status === 'Warn') {
    return <span className="text-yellow-500">⚠</span>
  } else if (status === 'Problem') {
    return <span className="text-red-500">✖</span>
  } else return <span className="text-light">{status}</span>
}

export const AppDatabricksPageContent = () => {
  const { applicationId } = useCurrentApplication()

  const [busy, setBusy] = useState<boolean>()
  const {
    getDatabricksApiGetDatabricksStatus,
    getDatabricksApiGetDatabricksApplicationStatus,
    putDatabricksApiProvisionApplication,
    postDatabricksApiPromoteApplication,
    putDatabricksApiInitialiseDatabricks,
    putDatabricksApiAddDefaultApplicationNotebook,
    postDatabricksApiPromoteNotebook,
  } = useRestClient()
  const { data: connectionStatus } = getDatabricksApiGetDatabricksStatus.useSuspenseQuery(undefined, {})

  const { data: applicationStatus } = getDatabricksApiGetDatabricksApplicationStatus.useSuspenseQuery({
    query: { applicationId },
  })

  const invalidateApplication = () => getDatabricksApiGetDatabricksApplicationStatus.invalidateQueries()

  const { mutateAsync: initialiseDatabricks } = putDatabricksApiInitialiseDatabricks.useMutation()
  const { mutateAsync: provisionApp } = putDatabricksApiProvisionApplication.useMutation()
  const { mutateAsync: promoteApp } = postDatabricksApiPromoteApplication.useMutation()
  const { mutateAsync: addDefaultNotebook } = putDatabricksApiAddDefaultApplicationNotebook.useMutation()
  const { mutateAsync: promoteNotebook } = postDatabricksApiPromoteNotebook.useMutation()

  const onInitialise = () => {
    setBusy(true)
    toast.info('Please wait while Databricks is initialised')
    initialiseDatabricks(
      {},
      {
        onError: (error) => {
          toast.error('Error initialising Databricks: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('Databricks initialisation complete')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onProvisionApp = () => {
    setBusy(true)
    toast.info('Please wait while the application is provisioned on Databricks')
    provisionApp(
      { query: { applicationId } },
      {
        onError: (error) => {
          toast.error('Error provisioning app: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: (data) => {
          toast.success('App provisioning complete')
          showDatabricksProvisionWarnings(data)
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onPromoteApp = () => {
    setBusy(true)
    toast.info('Please wait while the application is promoted on Databricks')
    promoteApp(
      { query: { applicationId } },
      {
        onError: (error) => {
          toast.error('Error promoting app: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('App promoting complete. You may need to Sync workspaces')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onPromoteNotebook = (notebookPath: string) => {
    setBusy(true)
    toast.info('Promoting notebook to production...')
    promoteNotebook(
      { query: { applicationId, notebookPath } },
      {
        onError: (error) => {
          toast.error('Error promoting notebook: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('Notebook promoted to production')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const onAddNotebook = () => {
    setBusy(true)
    toast.info('Please wait while a new notebook is added')
    addDefaultNotebook(
      { query: { applicationId } },
      {
        onError: (error) => {
          toast.error('Error adding notebook to app: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {
          toast.success('Added notebook successfully')
        },
        onSettled: () => {
          invalidateApplication()
          setBusy(false)
        },
      }
    )
  }

  const { items: connectionStatusItems } = connectionStatus
  const connectionContent = (
    <>
      {connectionStatusItems!.map(({ name, icon, messages }) => (
        <div className="flex flex-col whitespace-nowrap" key={name}>
          <span className="text-sm font-light">{name}:</span>
          <span className="text-base">
            {statusIcon(icon)} <span className="font-light">{messages}</span>
          </span>
        </div>
      ))}
    </>
  )

  const { developmentItems, productionItems, notebooks, productionNotebooks, devFolderUrl, prodFolderUrl } =
    applicationStatus
  const applicationContent = (
    <>
      <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
        {developmentItems!.map(({ name, icon, messages }) => (
          <div className="flex flex-col whitespace-nowrap" key={name}>
            <span className="text-sm font-light">{name}:</span>
            <span className="text-base">
              {statusIcon(icon)} <span className="font-light">{messages}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
        {productionItems!.map(({ name, icon, messages }) => (
          <div className="flex flex-col whitespace-nowrap" key={name}>
            <span className="text-sm font-light">{name}:</span>
            <span className="text-base">
              {statusIcon(icon)} <span className="font-light">{messages}</span>
            </span>
          </div>
        ))}
      </div>
    </>
  )

  const notebookContent = (
    <>
      <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {notebooks!.map(({ notebookPath, language, notebookUrl }) => (
          <div key={notebookPath} className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-background p-4 shadow">
            <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-muted p-2 text-foreground/50">
              <Notebook24Regular />
            </div>
            <div className="flex flex-col justify-between gap-1">
              <div className="text-base">{notebookPath}</div>
              <div className="text-xs font-light">{language}</div>
            </div>
            <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
              <Button
                disabled={busy}
                appearance="outline"
                size="small"
                icon={<Notebook24Regular />}
                onClick={() => onPromoteNotebook(notebookPath!)}
              >
                Promote
              </Button>
              <Button
                appearance="primary"
                size="small"
                icon={<Notebook24Regular />}
                onClick={() => window.open(notebookUrl, '_blank')}
              >
                Edit
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  )

  const productionNotebookContent = (
    <>
      <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {productionNotebooks!.map(({ notebookPath, language, notebookUrl }) => (
          <div key={notebookPath} className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-background p-4 shadow">
            <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-muted p-2 text-foreground/50">
              <Notebook24Regular />
            </div>
            <div className="flex flex-col justify-between gap-1">
              <div className="text-base">{notebookPath}</div>
              <div className="text-xs font-light">{language}</div>
            </div>
            <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
              <Button
                appearance="primary"
                size="small"
                icon={<Notebook24Regular />}
                onClick={() => window.open(notebookUrl, '_blank')}
              >
                View
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  )

  return (
    <>
      <div className="pt-5">
        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-background p-6 pt-4">
              <div className="flex flex-row items-center justify-between border-b">
                <h6>Databricks Connection / Configuration</h6>
              </div>
              <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
                {connectionContent}
                <div className="flex flex-grow flex-row gap-2 whitespace-nowrap">
                  <span className="flex-grow"></span>
                  <span className="flex flex-shrink flex-col gap-2 text-base">
                    <Button
                      disabled={busy}
                      appearance={connectionStatus?.items.some((x) => x.icon !== 'Check') ? 'primary' : 'secondary'}
                      onClick={() => onInitialise()}
                    >
                      Initialise Databricks
                    </Button>
                    <span className="text-sm font-light">Create necessary elements in Databricks</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-background p-6 pt-4">
              <div className="flex flex-row items-center justify-between border-b">
                <h6>Databricks Application Checks</h6>
              </div>
              {applicationContent}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-background p-6 pt-4">
              <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
                <div className="flex flex-col gap-2 whitespace-nowrap">
                  <span className="text-sm">Provision App:</span>
                  <span className="flex flex-col gap-2 text-base">
                    <Button
                      disabled={busy}
                      appearance={
                        applicationStatus.developmentItems?.some((x) => x.icon !== 'Check') ? 'primary' : 'secondary'
                      }
                      onClick={() => onProvisionApp()}
                    >
                      Provision App
                    </Button>
                    <span className="text-sm font-light">Create application folders and set up permissions</span>
                  </span>
                </div>
                <div className="flex flex-col gap-2 whitespace-nowrap">
                  <span className="text-sm">Promote App:</span>
                  <span className="flex flex-col gap-2 text-base">
                    <Button
                      disabled={busy}
                      appearance={
                        applicationStatus.productionItems?.some((x) => x.icon !== 'Check') ? 'primary' : 'secondary'
                      }
                      onClick={() => onPromoteApp()}
                    >
                      Promote App
                    </Button>
                    <span className="text-sm font-light">Copy development folder to production</span>
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_4fr] gap-4 rounded-lg bg-background p-4 shadow">
                  <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-muted p-2 text-foreground/50">
                    <FolderList20Regular fontSize={24} />
                  </div>
                  <div className="flex flex-col justify-between gap-1">
                    <Button
                      size="small"
                      icon={<FolderOpen24Regular />}
                      onClick={() => window.open(devFolderUrl, '_blank')}
                      disabled={devFolderUrl === ''}
                    >
                      Development
                    </Button>
                    <Button
                      size="small"
                      icon={<FolderOpen24Regular />}
                      onClick={() => window.open(prodFolderUrl, '_blank')}
                      disabled={prodFolderUrl === ''}
                    >
                      Production
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-b pb-1">
          <div>
            <h5>Notebooks</h5>
            <p>Manage the notebooks for your application below.</p>
          </div>
          <Button
            disabled={busy}
            appearance={applicationStatus.notebooks.length == 0 ? 'primary' : 'transparent'}
            icon={<Add24Regular />}
            onClick={() => onAddNotebook()}
          />
        </div>

        {notebookContent}

        <div className="mt-6 flex items-center justify-between border-b pb-1">
          <div>
            <h5>Production Notebooks</h5>
            <p>Notebooks currently deployed in the production folder.</p>
          </div>
        </div>

        {productionNotebooks!.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            No production notebooks found. Promote individual notebooks or use Promote App to copy all notebooks.
          </p>
        ) : (
          productionNotebookContent
        )}
      </div>
    </>
  )
}
